import { useEffect, useMemo, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const STATUS_STYLES = {
  PUBLISHED: 'bg-emerald-100 text-emerald-700',
  PENDING_REVIEW: 'bg-amber-100 text-amber-700',
  REJECTED: 'bg-red-100 text-red-700',
};

export default function AdminCoursesPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', country: '', status: '', fieldOfStudy: '', degreeLevel: '' });
  const [editingCell, setEditingCell] = useState(null); // { id, field }
  const [editValue, setEditValue] = useState('');

  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCourses = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    fetch(`${API_BASE}/admin/courses?${params.toString()}`, { headers })
      .then((r) => r.json())
      .then((json) => setCourses(json.success ? json.data : []))
      .finally(() => setLoading(false));
  };

  // Re-fetch only when the server-side filter (status) changes; everything
  // else (search, country, field, degree) is filtered client-side below.
  useEffect(fetchCourses, [filters.status]);

  const countries = useMemo(() => [...new Set(courses.map((c) => c.country))].sort(), [courses]);
  const fields = useMemo(() => [...new Set(courses.map((c) => c.fieldOfStudy))].filter(Boolean).sort(), [courses]);
  const levels = useMemo(() => [...new Set(courses.map((c) => c.degreeLevel))].filter(Boolean).sort(), [courses]);

  const shown = courses.filter((c) =>
    (!filters.search || `${c.courseName} ${c.fieldOfStudy}`.toLowerCase().includes(filters.search.toLowerCase())) &&
    (!filters.country || c.country === filters.country) &&
    (!filters.fieldOfStudy || c.fieldOfStudy === filters.fieldOfStudy) &&
    (!filters.degreeLevel || c.degreeLevel === filters.degreeLevel)
  );

  const startEdit = (id, field, currentValue) => {
    setEditingCell({ id, field });
    setEditValue(currentValue ?? '');
  };

  const saveEdit = async () => {
    const { id, field } = editingCell;
    const isNumeric = ['tuitionFeeAmount', 'agencyApplicationFee', 'durationYears'].includes(field);
    await fetch(`${API_BASE}/admin/courses/${id}`, {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: isNumeric ? (parseFloat(editValue) || null) : editValue }),
    });
    setEditingCell(null);
    fetchCourses();
  };

  const handleDelete = async (course) => {
    if (!window.confirm(`Delete "${course.courseName}"? This cannot be undone.`)) return;
    await fetch(`${API_BASE}/admin/courses/${course.id}`, { method: 'DELETE', headers });
    setCourses((prev) => prev.filter((c) => c.id !== course.id));
  };

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <AdminSidebar />
      <main className="pt-16 md:pt-2 md:ml-64 min-h-screen p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-[#00255a] mb-2">All Courses</h2>
          <p className="text-[#64748b]">Every course in the catalogue, regardless of status. Click a cell to edit it inline.</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm p-5 mb-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          <input
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search course or field…"
            className="col-span-2 px-4 py-2.5 rounded-lg border border-[#c4c6d1] outline-none focus:ring-2 focus:ring-[#00255a]/10 text-sm"
          />
          <select value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} className="px-4 py-2.5 rounded-lg border border-[#c4c6d1] outline-none text-sm">
            <option value="">All countries</option>
            {countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filters.fieldOfStudy} onChange={(e) => setFilters({ ...filters, fieldOfStudy: e.target.value })} className="px-4 py-2.5 rounded-lg border border-[#c4c6d1] outline-none text-sm">
            <option value="">All fields</option>
            {fields.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="px-4 py-2.5 rounded-lg border border-[#c4c6d1] outline-none text-sm">
            <option value="">All statuses</option>
            <option value="PUBLISHED">Published</option>
            <option value="PENDING_REVIEW">Pending Review</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </div>

        <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#f2f4f6]">
                <tr>
                  {['Course Name', 'Country', 'Degree', 'Field', 'Tuition', 'Duration (yrs)', 'Status', ''].map((h) => (
                    <th key={h} className="px-4 py-4 text-xs uppercase text-[#64748b] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {loading ? (
                  <tr><td colSpan="8" className="p-12 text-center text-[#64748b]">Loading…</td></tr>
                ) : shown.length === 0 ? (
                  <tr><td colSpan="8" className="p-12 text-center text-[#64748b]">No courses match these filters.</td></tr>
                ) : (
                  shown.map((course) => (
                    <tr key={course.id} className="hover:bg-[#f7f9fb] transition-colors">
                      <EditCell course={course} field="courseName" editingCell={editingCell} editValue={editValue} setEditValue={setEditValue} startEdit={startEdit} saveEdit={saveEdit} />
                      <td className="px-4 py-4 text-sm text-[#44474f] whitespace-nowrap">{course.country}</td>
                      <EditCell course={course} field="degreeLevel" editingCell={editingCell} editValue={editValue} setEditValue={setEditValue} startEdit={startEdit} saveEdit={saveEdit} />
                      <EditCell course={course} field="fieldOfStudy" editingCell={editingCell} editValue={editValue} setEditValue={setEditValue} startEdit={startEdit} saveEdit={saveEdit} />
                      <EditCell course={course} field="tuitionFeeAmount" editingCell={editingCell} editValue={editValue} setEditValue={setEditValue} startEdit={startEdit} saveEdit={saveEdit} prefix={course.tuitionFeeCurrency ? `${course.tuitionFeeCurrency} ` : '$'} placeholder="Not set" />
                      <EditCell course={course} field="durationYears" editingCell={editingCell} editValue={editValue} setEditValue={setEditValue} startEdit={startEdit} saveEdit={saveEdit} placeholder="—" />
                      <td className="px-4 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_STYLES[course.status] || 'bg-slate-100 text-slate-600'}`}>
                          {course.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button onClick={() => handleDelete(course)} className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors" title="Delete course">
                          <span className="material-symbols-outlined text-[20px]">delete</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function EditCell({ course, field, editingCell, editValue, setEditValue, startEdit, saveEdit, prefix, placeholder }) {
  const isEditing = editingCell?.id === course.id && editingCell?.field === field;
  const value = course[field];

  if (isEditing) {
    return (
      <td className="px-4 py-4">
        <div className="flex items-center gap-1">
          <input
            autoFocus
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
            onBlur={saveEdit}
            className="w-full px-2 py-1 border border-[#00255a] rounded text-sm outline-none"
          />
        </div>
      </td>
    );
  }

  return (
    <td
      onClick={() => startEdit(course.id, field, value)}
      className="px-4 py-4 text-sm text-[#0f172a] cursor-pointer hover:bg-[#f2f4f6] transition-colors whitespace-nowrap"
    >
      {value ? `${prefix || ''}${value}` : <span className="text-[#94a3b8] italic">{placeholder || 'Empty'}</span>}
    </td>
  );
}